//npm packages
import React, { useEffect, useCallback, memo, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm, initialize, change, reset } from 'redux-form';
import { Col, Row, Button } from 'reactstrap';
import swal from 'sweetalert2';
import { useHistory } from 'react-router-dom';
import { faDownload, faPencilRuler } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { equipmentList, licenseForm, LICENSEFORM } from '../../redux/actions';
import { InputField, SelectField, FormBase, Validator } from '../../components';
import consts from '../../consts';
import strings from '../../globalization';
import utility from '../../utility';
import EquipmentList from '../Equipment/EquipmentList';
import BasicRowColGridDefault, {
  defaultProps
} from '../../utility/BasicRowColGrid';
import { debounce } from 'lodash';

const { ProductType } = consts;

const BasicRowColGrid = props => (
  <BasicRowColGridDefault
    {...{
      fieldNameCol: {
        ...defaultProps.fieldNameCol,
        xl: 4
      },
      fieldCol: {
        ...defaultProps.fieldCol,
        xl: 8
      }
    }}
    {...props}
  />
);
const findValue = (arr, str) => {
  return arr && arr.find(item => item.label === str);
};
const productTypeDefaultValue = [
  {
    value: '62318c69a51ff2ec2bdae2ec',
    label: 'CAT Export',
    isDisabled: false
  },
  {
    isDisabled: false,
    label: 'CAT Sync',
    value: '62318c17a51ff2ec2bdae2eb'
  }
];
const allowedIdsForLicenseType = consts.allowedIdsForLicenseType;

// Conflicting product with CAT Edge
const conflictingProductTypes = [
  ProductType.catexport,
  ProductType.catviewer,
  ProductType.catendo,
  ProductType.catrfid
];

const catV5Values = [ProductType.catendo, ProductType.catrfid];
const version4Id = consts.Version4Id;

const LicenseForm = memo(props => {
  const {
    data: initialValues = {},
    success,
    info,
    CustomError,
    err
  } = useSelector(state => state.licenseFormData.data);
  const { userDetail: loginData } = useSelector(
    state => state.loginData.data || {}
  );
  const { isDark } = useSelector(state => state.settingData);
  const comboType = useSelector(state => state.comboTypes);

  const shouldUpdate = useMemo(
    () =>
      (success &&
        ['RecordSavedSuccessfully', 'RecordUpdatedSuccessfully'].includes(
          info
        )) ||
      CustomError,
    [info, CustomError]
  );

  const productTypeComboV5 =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.ProductType
    ) || [];

  const productTypeComboV4 = productTypeComboV5.filter(
    product => !catV5Values.includes(product.value)
  );

  const [state, setState] = useState({
    showSyncVersion: false,
    showViewerVerison: false,
    productTypeOptions: [],
    licenseType: false
  });

  const dispatch = useDispatch();
  const history = useHistory();
  const { anyTouched, error } = props;
  const { params } = props.match;
  const { Facility: facility = {} } = initialValues || {};
  const licBtnDisable = useMemo(
    () =>
      (initialValues && initialValues.disableDownload) || params.id === 'new',
    [initialValues, params.id]
  );

  const shouldDownload = useMemo(
    () =>
      loginData && !consts.UserRoleId.Specialist.includes(loginData.UserRole),
    [loginData]
  );

  useEffect(() => {
    if (params.id === 'new' && initialValues._id) {
      props.history.replace({
        pathname: `/licenseDetail/${initialValues._id}`
      });
    }
  }, [initialValues]);

  useEffect(() => {
    if (Object.keys(initialValues).length) {
      let facility = null;
      let initalProductTypes = productTypeDefaultValue;

      if (initialValues.Facility) {
        facility = initialValues.Facility._id;
      }

      const productTypes = initialValues.ProductType;
      if (Array.isArray(productTypes) && productTypes.length) {
        initalProductTypes = productTypeComboV5.filter(comboOption =>
          productTypes.includes(comboOption.value)
        );
      }

      const newState = {
        showSyncVersion: false,
        showViewerVerison: false,
        licenseType: false
      };
      if (initialValues.CatSoftwareVersion === version4Id) {
        newState.showSyncVersion = true;
      }
      if (allowedIdsForLicenseType.includes(initialValues.CatSoftwareVersion)) {
        newState.licenseType = true;
      }
      if (
        Array.isArray(productTypes) &&
        productTypes.includes(ProductType.catviewer)
      ) {
        newState.showViewerVerison = true;
      }

      setState(prev => ({ ...prev, ...newState }));

      dispatch(
        initialize('licenseForm', {
          ...initialValues,
          Facility: facility,
          ProductType: initalProductTypes,
          ExpirationDate:
            initialValues.ExpirationDate ||
            dayjs()
              .add(5, 'years')
              .endOf('month')
              .add(1, 'day')
        })
      );
    }
  }, [initialValues]);

  useEffect(() => {
    // fetching license form data
    dispatch(
      licenseForm({
        action: 'load',
        _id: params.id,
        comboType: [
          'Language',
          'ProductType',
          'Status',
          'CatSync',
          'CatViewer',
          'Facility',
          'LicenseType',
          'CatVersion',
          'CatSoftwareVersion'
        ]
      })
    );
    // fetching equipment list data
    if (params.id !== 'new') {
      let param = {
        filters: { LicenseId: params.id },
        action: 'list',
        limit: 25,
        skip: 0,
        sort: { sort: -1 },
        comboType: [
          'EquipmentType',
          'ConnectivityType',
          'EquipmentModel',
          'TransferPDF',
          'EquipmentState',
          'User'
        ]
      };
      dispatch(equipmentList(param));
    }
  }, [window.location.hash, shouldUpdate]);

  useEffect(() => {
    if (CustomError && err) {
      swal
        .fire({
          title: err,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        })
        .then(result => {
          if (result.isConfirmed) {
            dispatch(
              licenseForm({
                action: 'otherAction',
                otherAction: 'UpdateLicense',
                Id: params.id,
                ...initialValues
              })
            );
          } else {
            dispatch(reset('licenseForm'));
          }
        });
    }
  }, [CustomError]);

  const disableSave = useMemo(() => !anyTouched || error, [anyTouched, error]);

  const onSubmit = useCallback(
    values => {
      const comboFields = [
        { name: 'DefaultLanguage', isArray: false },
        { name: 'ProductType', isArray: true },
        { name: 'IsActive', isArray: false },
        { name: 'Facility', isArray: false },
        { name: 'CatSyncVersion', isArray: false },
        { name: 'CatViewerVersion', isArray: false },
        { name: 'CatSoftwareVersion', isArray: false }
      ];
      if (allowedIdsForLicenseType.includes(values.CatSoftwareVersion)) {
        comboFields.push({ name: 'LicenseType', isArray: false });
      }
      const data = utility.processDataBeforeSave(values, comboFields);
      if (!allowedIdsForLicenseType.includes(data.CatSoftwareVersion)) {
        data.LicenseType = '';
        initialValues.LicenseType = '';
      } else if (
        allowedIdsForLicenseType.includes(data.CatSoftwareVersion) &&
        typeof data.LicenseType === 'object'
      ) {
        data.LicenseType = data.LicenseType.value;
      }
      Object.assign(data, { tableName: 'License' });
      delete data.Facility;
      dispatch(
        licenseForm({
          action: params.id.toLowerCase() === 'new' ? 'add' : 'update',
          ...initialValues,
          ...data
        })
      );
    },
    [initialValues]
  );

  const catViewerOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.CatViewer
    ) || [];

  const catSyncOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.CatSync
    ) || [];

  const catOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.CatSoftwareVersion
    ) || [];

  const statusOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.Status
    ) || [];

  const facilityOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'Name' },
      params.id === 'new'
        ? comboType.Facility
        : [
            {
              _id: facility && facility._id,
              Name: facility && facility.Name
            }
          ]
    ) || [];

  const languageOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.Language
    ) || [];

  const onFacilityClick = values => {
    if (values && values.Facility) {
      let id = values.Facility._id;
      history.push({ pathname: `/site/${id}` });
    } else {
      history.push({ pathname: `/site/new` });
    }
  };

  const onDownloadLicense = () => {
    const hasMachineKey = initialValues.MachineKey;
    const hasCATSync =
      hasMachineKey &&
      initialValues.ProductType &&
      initialValues.ProductType.includes(consts.ProductType.catsync);
    const checkFirstInput =
      initialValues.Facility.City !== '' &&
      initialValues.Facility.State !== '' &&
      initialValues.Facility.CountryCode !== '';
    const checkInput = hasCATSync
      ? checkFirstInput &&
        initialValues.Facility.CommonName !== '' &&
        initialValues.Facility.OrganizationalUnit !== '' &&
        initialValues.Facility.EmailAddress !== ''
      : checkFirstInput;
    const canGenerateCert =
      initialValues.Facility.CommonName &&
      initialValues.Facility.OrganizationalUnit &&
      initialValues.Facility.EmailAddress &&
      hasCATSync;

    const hasCATSoftwareVersionSelected = Boolean(
      initialValues.CatSoftwareVersion
    );

    let { id } = params;
    if (hasCATSoftwareVersionSelected && (checkInput || hasCATSync)) {
      const defaultCATSoftwareVersion =
        initialValues.defaultCATSoftwareVersion.CATSoftwareVersion;
      const selectedCATSoftwareVersion =
        initialValues.selectedCATSoftwareVersion.CATSoftwareVersion;
      const isDifferentFromDefault =
        selectedCATSoftwareVersion !== defaultCATSoftwareVersion;

      const proceedWithLicense = () => {
        if (canGenerateCert) {
          swal
            .fire(strings.Warning, strings.LicensePopUp, 'warning')
            .then(({ isConfirmed }) => {
              if (isConfirmed) {
                utility.onDownloadCertificate(initialValues);
              }
            });
        } else {
          utility.onLicenseDownload(id, initialValues);
        }
      };

      if (isDifferentFromDefault) {
        swal
          .fire({
            title: `Are you sure the Customer is on CAT ${selectedCATSoftwareVersion}? The default CAT Software Version is CAT ${defaultCATSoftwareVersion}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
          })
          .then(result => {
            if (result.isConfirmed) {
              proceedWithLicense();
            }
          });
      } else {
        proceedWithLicense();
      }
    } else {
      const emptyFields = [];
      if (!checkFirstInput) {
        emptyFields.push('City', 'State', 'Country', 'CAT Software Version');
      }

      if (hasCATSync) {
        if (initialValues.Facility.CommonName === '') {
          emptyFields.push('Common Name');
        }
        if (initialValues.Facility.OrganizationalUnit === '') {
          emptyFields.push('Organizational Unit');
        }
        if (initialValues.Facility.EmailAddress === '') {
          emptyFields.push('Email Address');
        }
      }

      let subMessage = 'site';

      if (!hasCATSoftwareVersionSelected) {
        emptyFields.push('CAT Software Version');
        subMessage =
          emptyFields.length > 1
            ? 'License details and site'
            : 'License details';
      }

      swal.fire(
        strings.Warning,
        `Please enter : ${emptyFields.join(', ')} on ${subMessage} form`,
        'warning'
      );
    }
  };

  const advanceButtons = (
    <Button
      title={licBtnDisable ? strings.LicenseDownloadButton : strings.Download}
      color="primary"
      onClick={onDownloadLicense}
      disabled={licBtnDisable}
      style={{ float: 'right' }}
    >
      {' '}
      <FontAwesomeIcon icon={faDownload} />
    </Button>
  );

  const onChangeProduct = debounce((event, newValue, previousValue, name) => {
    const changes = newValue.filter(item =>
      previousValue.every(
        prevItem => (prevItem.value ? prevItem.value : prevItem) !== item.value
      )
    );
    const isCatEdge = changes.some(item => item.value === ProductType.catedge);
    const isConfilictingProduct = changes.some(item =>
      conflictingProductTypes.includes(item.value)
    );

    if (isCatEdge) {
      newValue = newValue.filter(
        item => !conflictingProductTypes.includes(item.value)
      );
    }

    if (isConfilictingProduct) {
      newValue = newValue.filter(item => item.value !== ProductType.catedge);
    }
    if (!newValue) newValue = productTypeDefaultValue;

    const isCatEdgeOnly =
      newValue.length === 1 && newValue[0].value === ProductType.catedge;
    const isCatSyncOnly =
      newValue.length === 1 && newValue[0].value === ProductType.catsync;
    const isCatSyncEdgeOnly =
      newValue.length === 2 &&
      newValue.every(item =>
        [ProductType.catedge, ProductType.catsync].includes(item.value)
      );

    const hasCatViewer = newValue.some(
      item => item.value === ProductType.catviewer
    );
    setState(prev => ({ ...prev, showViewerVerison: hasCatViewer }));

    if (isCatEdgeOnly || isCatSyncOnly || isCatSyncEdgeOnly) {
      swal.fire(strings.Warning, strings.EquipmentWarning, 'warning');
    }
    dispatch(change('licenseForm', name, newValue));
  }, 100);

  const onChange = (event, newValue, previousValue, name) => {
    newValue = typeof newValue === 'object' ? newValue.value : newValue;
    dispatch(change('licenseForm', name, newValue));
  };

  const onChangeVersion = debounce((event, newValue, previousValue, name) => {
    dispatch(change('licenseForm', name, newValue));
    dispatch(change('licenseForm', 'ProductType', productTypeDefaultValue));
    let newState = {
      showSyncVersion: false,
      productTypeOptions: productTypeComboV5
    };
    if (newValue.value === version4Id) {
      newState = {
        showSyncVersion: true,
        productTypeOptions: productTypeComboV4
      };
    }
    if (allowedIdsForLicenseType.includes(newValue.value)) {
      newState.licenseType = true;
    }
    setState(newState);
  }, 100);

  const onStatusChange = value => {
    if (value && value.value !== consts.LicActive) {
      swal.fire(strings.Warning, strings.StatusWarn, 'warning');
    }
  };

  const onFormCancel = () => {
    history.push({ pathname: `/managelicense` });
  };

  const productTypes = useMemo(() => {
    const isVersion4 = initialValues.CatSoftwareVersion === version4Id;
    return state.productTypeOptions.length
      ? state.productTypeOptions
      : isVersion4
      ? productTypeComboV4
      : productTypeComboV5;
  }, [initialValues, state.productTypeOptions]);

  const licenseTypeOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.LicenseType
    ) || [];

  let productTypeOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.ProductType
    ) || [];
  if (initialValues.CatVersion === version4Id) {
    productTypeOptions = productTypeOptions.filter(
      item => !catV5Values.includes(item.value)
    );
  }

  return (
    <FormBase
      {...props}
      isDark={isDark}
      onSubmit={onSubmit}
      title={strings.LicenseForm}
      subTitle={initialValues && facility.Name}
      actionInfo={{ action: licenseForm, actionName: LICENSEFORM }}
      isFromLicense={true}
      advanceButtons={shouldDownload ? '' : advanceButtons}
      form="licenseForm"
      disablePristine={!disableSave}
      onFormCancel={onFormCancel}
    >
      <Row sm="12" md="12" lg="12">
        <BasicRowColGrid>
          {strings.SiteName}
          <Field
            name="FacilityId"
            type="select"
            isDisabled={params.id !== 'new'}
            component={SelectField}
            placeholder={strings.FacilityName}
            options={facilityOptions}
            validate={params.id === 'new' ? Validator.required : undefined}
            value={initialValues.FacilityId}
            defaultValue={initialValues.FacilityId}
          />
          <span className="addableInput-btn">
            <Button
              title={strings.EditSite}
              color="success"
              type="button"
              onClick={() => {
                onFacilityClick(initialValues);
              }}
            >
              <FontAwesomeIcon icon={faPencilRuler} />
            </Button>
          </span>
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.DefaultLanguage}
          <Field
            name="DefaultLanguage"
            type="select"
            component={SelectField}
            placeholder={strings.DefaultLanguage}
            options={languageOptions}
            validate={Validator.required}
            onChange={onChange}
            value={initialValues.DefaultLanguage}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.CatSoftwareVersion}
          <Field
            name="CatSoftwareVersion"
            type="select"
            component={SelectField}
            placeholder={strings.CatSoftwareVersion}
            options={catOptions}
            validate={Validator.required}
            onChange={onChangeVersion}
            value={initialValues.CatSoftwareVersion}
          />
        </BasicRowColGrid>
        {state.licenseType && (
          <BasicRowColGrid>
            {strings.LicenseType}
            <Field
              name="LicenseType"
              type="select"
              defaultValue={'Client License Type'}
              component={SelectField}
              placeholder={strings.LicenseType}
              options={licenseTypeOptions}
              validate={Validator.required}
            />
          </BasicRowColGrid>
        )}
        <BasicRowColGrid>
          {strings.ProductType}
          <Field
            name="ProductType"
            type="select"
            isMulti={true}
            component={SelectField}
            placeholder={strings.ProductType}
            options={productTypes}
            validate={Validator.required}
            onChange={onChangeProduct}
            value={initialValues.ProductType}
          />
        </BasicRowColGrid>
        {state.showSyncVersion && (
          <BasicRowColGrid>
            {strings.CatSyncVersion}
            <Field
              name="CatSyncVersion"
              type="select"
              defaultValue={{
                value: '62597d8fec95ff1d50fecbc6',
                label: '2.1',
                isDisabled: false
              }}
              component={SelectField}
              placeholder={strings.CatSyncVersion}
              options={catSyncOptions}
              onChange={onChange}
              value={initialValues.CatSyncVersion}
            />
          </BasicRowColGrid>
        )}
        {state.showViewerVerison && (
          <BasicRowColGrid>
            {strings.CatViewerVersion}
            <Field
              name="CatViewerVersion"
              type="select"
              component={SelectField}
              placeholder={strings.CatViewerVersion}
              options={catViewerOptions}
              onChange={onChange}
              value={initialValues.CatViewerVersion}
            />
          </BasicRowColGrid>
        )}
        <BasicRowColGrid>
          {strings.MachineKey}
          <Field
            name="MachineKey"
            type="text"
            component={InputField}
            placeholder={strings.MachineKey}
            onChange={onChange}
            value={initialValues.MachineKey}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.Status}
          <Field
            name="IsActive"
            type="select"
            component={SelectField}
            placeholder={strings.State}
            options={statusOptions}
            onChange={onStatusChange}
            validate={Validator.required}
            value={initialValues.IsActive}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.InitialSOA}
          <Field
            name="InitialSOA"
            type="text"
            component={InputField}
            placeholder={strings.InitialSOA}
            maxLength="100"
            onChange={onChange}
            value={initialValues.InitialSOA}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.MostRecentSOA}
          <Field
            name="MostRecentSOA"
            type="text"
            component={InputField}
            placeholder={strings.MostRecentSOA}
            maxLength="100"
            onChange={onChange}
            value={initialValues.MostRecentSOA}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.Notes}
          <Field
            name="Notes"
            type="textarea"
            component={InputField}
            placeholder={strings.Notes}
            rows="5"
            className="wt-resize"
            onChange={onChange}
            value={initialValues.Notes}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.ExpirationDate}
          <Field
            name="ExpirationDate"
            type="date"
            component={InputField}
            placeholder={strings.ExpirationDate}
            min={
              dayjs()
                // .add(5, 'years')
                // .endOf('month')
                .add(1, 'day')
                .toISOString()
                .split('T')[0]
            }
            onChange={onChange}
            value={initialValues.ExpirationDate}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.GracePeriod}
          <Field
            name="GracePeriod"
            type="number"
            min={1}
            component={InputField}
            placeholder={strings.GracePeriod}
            validate={Validator.required}
            onChange={onChange}
            value={initialValues.GracePeriod}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.HardPeriod}
          <Field
            name="HardStop"
            type="number"
            min={1}
            component={InputField}
            placeholder={strings.HardPeriod}
            validate={Validator.required}
            onChange={onChange}
            value={initialValues.HardPeriod}
          />
        </BasicRowColGrid>
      </Row>
    </FormBase>
  );
});

const LicenseReduxForm = reduxForm({
  form: 'licenseForm',
  enableReinitialize: false,
  destroyOnUnmount: false,
  keepValues: false
})(LicenseForm);

const LicenseFormPanel = props => {
  const { params } = props.match;

  const { initialValues } = useSelector(({ licenseFormData }) => {
    return { initialValues: licenseFormData.data.data || {} };
  }, shallowEqual);

  const onDblClick = (rowIdx, row, column, config) => {
    const { detailRoute, parentRoute } = config;
    props.history.push({
      pathname: `/${detailRoute || parentRoute}/${row._id}/${row.LicenseId}`
    });
  };

  const screenSize = useMemo(() => {
    const isFullScreenForm = Boolean(
      params.id === 'new' ||
        initialValues.isCatSyncOnly ||
        initialValues.isCatEdgeOnly ||
        initialValues.isCatSyncEdgeOnly
    );
    return isFullScreenForm ? 12 : 6;
  }, [initialValues, params.id]);

  const hideEquipmentList = useMemo(() => {
    return Boolean(
      initialValues.isCatSyncOnly ||
        initialValues.isCatEdgeOnly ||
        initialValues.isCatSyncEdgeOnly
    );
  }, [initialValues]);
  return (
    <Row className="license-form">
      <Col sm={12} xl={screenSize} className="mb-2">
        <LicenseReduxForm {...props} />
      </Col>
      <br />
      {!hideEquipmentList && (
        <Col sm={12} xl={screenSize} className="mb-2">
          <EquipmentList
            {...props}
            key={Math.random()}
            parentId={params.id}
            baseFilter={{ LicenseId: params.id }}
            onDblClick={onDblClick}
            disableAdd={params.id.toLowerCase() === 'new'}
          />
        </Col>
      )}
    </Row>
  );
};

export default LicenseFormPanel;
